import _ from 'lodash';
import { useLog } from '@tradingblock/components';
import { TradingBlockLegacyUrlsLookup } from '@tradingblock/types';
import { TradingEnvironments, TradingEnvironment } from './constants';

const isTruthyValue = (value: string | undefined) =>
  value && ['1', 'true', 'True', 'TRUE', 'yes'].includes(value) ? true : false;

/** Ensures a trailing slash for legacy embed URLs. */
const normalizeLegacyTradingBlockBaseUrl = (url: string): string => {
  const t = url.trim();
  return t.endsWith('/') ? t : `${t}/`;
};

/** All dev / local builds (tb and mb) use the shared staging legacy host. */
const LEGACY_STAGING_DEV = 'https://legacystaging.tradingblock.com/';
const LEGACY_TB_BETA_PROD = 'https://legacy.tradingblock.com/';
const LEGACY_MB_BETA_PROD = 'https://legacy.moneyblock.com/';

/**
 * Base URL for the embedded legacy site (iframe).
 * Override with `REACT_APP_LEGACY_TRADINGBLOCK_URL`.
 * Defaults: dev (all) → legacystaging.tradingblock.com; beta/prod tb → legacy.tradingblock.com;
 * beta/prod mb → legacy.moneyblock.com.
 */
const resolveLegacyTradingBlockUrl = (): string => {
  const explicit = process.env.REACT_APP_LEGACY_TRADINGBLOCK_URL;
  if (explicit !== undefined && explicit.trim() !== '') {
    return normalizeLegacyTradingBlockBaseUrl(explicit);
  }
  const env = process.env.REACT_APP_ENVIRONMENT;
  const isMoneyBlock = process.env.REACT_APP_TB_SITE_GROUP === 'mb';

  if (env === 'production' || env === 'beta') {
    return isMoneyBlock ? LEGACY_MB_BETA_PROD : LEGACY_TB_BETA_PROD;
  }

  return LEGACY_STAGING_DEV;
};

/**
 * URL for the legacy iframe SSO flow. Pass `state.auth.apiToken` (RestHub API GUID from Redux).
 * Example: `https://legacystaging.tradingblock.com/account/securitieslogin.aspx?AuthGuid=<token>`.
 */
export const buildLegacyTradingBlockEmbedUrl = (restHubApiToken: string | undefined): string => {
  const base = resolveLegacyTradingBlockUrl().replace(/\/+$/, '');
  const path = TradingBlockLegacyUrlsLookup.LegacySiteLoginUrl;
  const trimmed = typeof restHubApiToken === 'string' ? restHubApiToken.trim() : '';
  if (trimmed !== '') {
    const q = new URLSearchParams();
    q.set('AuthGuid', trimmed);
    return `${base}${path}?${q.toString()}`;
  }
  console.warn(
    '[config] buildLegacyTradingBlockEmbedUrl: missing RestHub token; loading securities login without SSO params'
  );
  return `${base}${path}`;
};

/**
 * Legacy iframe + menu link: enabled only when `REACT_APP_ENVIRONMENT` is `local` or `dev`.
 * Always off for `beta` and `production`. In local/dev, set `REACT_APP_SHOW_LEGACY_TRADINGBLOCK_EMBED=false` to hide.
 */
const resolveShowLegacyTradingBlockEmbed = (): boolean => {
  const env = process.env.REACT_APP_ENVIRONMENT;
  if (env === 'beta' || env === 'production') {
    return false;
  }
  if (env !== 'local' && env !== 'dev') {
    return false;
  }
  const explicit = process.env.REACT_APP_SHOW_LEGACY_TRADINGBLOCK_EMBED;
  if (explicit !== undefined && explicit.trim() !== '' && !isTruthyValue(explicit)) {
    return false;
  }
  return true;
};

function valueOrDefault<T = string>(key: string, getValue?: (val: string | undefined) => T | undefined) {
  return (defaultValue: T): T => {
    const keyVal = isTruthyValue(process.env.REACT_APP_IS_BETA) ? `REACT_APP_BETA_${key}` : `REACT_APP_${key}`;
    const strVal = process.env[keyVal];
    const envVal: T | undefined = getValue ? getValue(strVal) : (strVal as any);
    return _.isNil(envVal) ? defaultValue : envVal;
  };
}
/** When true, TBFE-2301 behavior is active (cached public-key verify, bootstrap phases, safer reload). Active for `dev` and `local` only — not beta/production. */
const resolveTbfe2301ResilientDashboardAuth = (): boolean => {
  const env = process.env.REACT_APP_ENVIRONMENT;
  return env === 'dev' || env === 'local';
};

const envConfig = {
  schemaVersion: process.env.REACT_APP_SCHEMA_VERSION as string,
  uiApi: `${process.env.REACT_APP_UI_API_URL as string}${
    (isTruthyValue(process.env.REACT_APP_IS_VIRTUAL) as boolean) ? '/virtual' : ''
  }`,
  tradingApi: process.env.REACT_APP_TRADING_API_URL as string,
  tradingApiWebsockets: process.env.REACT_APP_TRADING_API_WS_URL as string,
  tradingApiWebsocketsNew: process.env.REACT_APP_TRADING_API_WS_URL_NEW as string,
  marketNewsWebsockets: process.env.REACT_APP_MARKET_NEWS_WS_URL as string,
  orderStatusWebsockets: process.env.REACT_APP_ORDER_STATUS_WS_URL as string,
  expiringTokensWebsockets: process.env.REACT_APP_EXPIRING_TOKENS_WS_URL as string,
  cashieringWebsockets: process.env.REACT_APP_CASHIERING_WS_URL as string,
  loginCountWebsockets: process.env.REACT_APP_LOGIN_COUNT_WS_URL as string,
  authRedirect: process.env.REACT_APP_AUTH_REDIRECT as string,
  loginUrl: process.env.REACT_APP_LOGIN_URL as string,
  applicationUrl: process.env.REACT_APP_APPLICATION_URL as string,
  liveTradingUrl: process.env.REACT_APP_LIVE_URL as string,
  virtualTradingUrl: process.env.REACT_APP_VIRTUAL_URL as string,
  isProduction: process.env.REACT_APP_ENVIRONMENT === 'production',
  useOrdersSearchEndpoint:
    process.env.REACT_APP_ENVIRONMENT !== 'production' || isTruthyValue(process.env.REACT_APP_IS_VIRTUAL as string),
  gaUaTrackingCode: process.env.REACT_APP_GA_UA_TRACKING_CODE as string,
  ga4TrackingCode: (process.env.REACT_APP_GA_4_TRACKING_CODE as string) || '',
  ga4VTTrackingCode: (process.env.REACT_APP_VT_GA_4_TRACKING_CODE as string) || '',
  eventLogWhitelist: ['@order/created'],
  isVirtual: isTruthyValue(process.env.REACT_APP_IS_VIRTUAL as string),
  subdirectory: (process.env.REACT_APP_TB_SUBDIRECTORY as string)
    ? (process.env.REACT_APP_TB_SUBDIRECTORY as string)
    : '',
  siteGroup: process.env.REACT_APP_TB_SITE_GROUP as string,
  legacyTradingBlockUrl: resolveLegacyTradingBlockUrl(),
  showLegacyTradingBlockEmbed: resolveShowLegacyTradingBlockEmbed(),
  /** TBFE-2301: dev + local only; beta/production use legacy auth bootstrap and beforeunload token handling */
  tbfe2301ResilientDashboardAuth: resolveTbfe2301ResilientDashboardAuth(),
  perf: {
    batchedQuotes:
      process.env.REACT_APP_PERF_BATCHED_QUOTES === undefined
        ? true
        : isTruthyValue(process.env.REACT_APP_PERF_BATCHED_QUOTES as string),
    quotesBatchIntervalMs: (() => {
      const v = Number(process.env.REACT_APP_QUOTES_BATCH_INTERVAL_MS || '');
      return Number.isFinite(v) && v > 0 ? v : 400;
    })(),
  },
};

const notOverridableConfig = {
  TradingEnvironment: valueOrDefault<TradingEnvironments>(
    'TRADING_ENVIRONMENT',
    (val: string | undefined): TradingEnvironments => {
      const tEnv = _.toLower(val || '');
      switch (tEnv) {
        case 'local':
          return TradingEnvironment.local;
        case 'dev':
        case 'development':
        case 'staging':
          return TradingEnvironment.dev;
        case 'beta':
          return TradingEnvironment.beta;
        default:
          return TradingEnvironment.production;
      }
    }
  )(TradingEnvironment.dev),
};

const combinedConfig = {
  ...envConfig,
  ...notOverridableConfig,
};
const log = useLog('config');
log('using loaded config %o', combinedConfig);
type EnvConfigType = typeof envConfig;
type NotOverridableConfig = typeof notOverridableConfig;
export const Config: EnvConfigType & NotOverridableConfig = combinedConfig;
export default Config;
